import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { Link } from "react-router-dom";
import { ExportSquare } from "iconsax-reactjs";
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

const getRemark = (txn = {}) => {
    const normalRemark = txn?.narration ?? txn?.remark ?? txn?.description;
    return normalRemark ?? "—";
};

const normalizeStatus = (raw, paymentStatus) => {
    const s = String(raw || "").toLowerCase();
    const p = String(paymentStatus || "").toLowerCase();

    if (s === "success" || s === "completed" || s === "complete") return "Completed";
    if (p === "pending") return "Pending";
    if (s === "pending" || s === "processing") return "Processing";

    return raw || "—";
};

const normalizeKey = (value) => String(value || "").trim().toLowerCase();

const formatRate = (rate, fromCurrency, toCurrency) => {
    const numericRate = Number(rate);
    if (!Number.isFinite(numericRate) || !fromCurrency || !toCurrency) return "—";
    return `${numericRate.toLocaleString(undefined, {
        maximumFractionDigits: 8,
    })} ${fromCurrency} / ${toCurrency}`;
};

const formatAmount = (amount, currency) => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return "—";
    return `${numericAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} ${currency || ""}`.trim();
};

export const getOtcTransactionState = (txn = {}) => {
    const otc = txn.otc || {};
    const history = Array.isArray(otc.history) ? otc.history : [];
    const keys = [otc.status, txn.status, ...history.flatMap((item) => [item.action, item.status])]
        .map(normalizeKey)
        .filter(Boolean);
    const hasKey = (...values) => values.some((value) => keys.includes(value));
    const accepted = hasKey("offer_accepted") || Boolean(otc.acceptedAt);
    const completed = ["success", "completed", "complete"].includes(normalizeKey(txn.status));
    const offerExpired =
        !accepted && otc.expiresAt && dayjs(otc.expiresAt).isBefore(dayjs());

    let phase = "under_review";
    if (hasKey("request_rejected", "rejected")) phase = "rejected";
    else if (hasKey("offer_declined", "declined")) phase = "declined";
    else if (hasKey("cancelled", "canceled", "request_cancelled", "request_canceled")) {
        phase = "cancelled";
    } else if (hasKey("offer_expired", "expired") || offerExpired) phase = "expired";
    else if (completed && normalizeKey(otc.status) !== "awaiting_funding") phase = "completed";
    else if (accepted || hasKey("awaiting_funding", "external_settlement_initiated")) {
        phase = "accepted";
    } else if (hasKey("request_approved", "offer_sent") || otc.offeredRate != null) {
        phase = "offer_sent";
    }

    const closed = ["rejected", "declined", "expired", "cancelled"].includes(phase);
    return {
        phase,
        accepted,
        displayStatus: closed ? "Closed" : phase === "completed" ? "Completed" : "Processing",
        canCancel: phase === "under_review",
        canRespondToOffer: phase === "offer_sent",
        canComplete: phase === "accepted",
    };
};

const getHistoryRows = (value) => {
    if (!value || typeof value !== "object") return [];
    if (Array.isArray(value)) return value;

    const candidates = [
        value.history,
        value.updates,
        value.statusHistory,
        value.otc?.history,
    ];
    return candidates.find(Array.isArray) || [];
};

export const getOtcRequestId = (value = {}) => {
    const rawOtc = value.raw?.otc;
    const raw = value.raw || value;
    const isOtcRecord =
        value.isOtc === true ||
        normalizeKey(raw.source) === "otc" ||
        normalizeKey(raw.type) === "otc" ||
        normalizeKey(raw.TransactionType) === "otc";
    const candidates = [
        value.otcId,
        value.otc_id,
        value.otcRequestId,
        value.otc_request_id,
        value.otcReference,
        typeof value.otc === "string" ? value.otc : null,
        value.otc?._id,
        value.otc?.id,
        value.raw?.otcId,
        value.raw?.otc_id,
        value.raw?.otcRequestId,
        value.raw?.otcReference,
        typeof rawOtc === "string" ? rawOtc : null,
        rawOtc?._id,
        rawOtc?.id,
        isOtcRecord ? value.id : null,
        isOtcRecord ? value._id : null,
        isOtcRecord ? value.raw?._id : null,
    ];
    return candidates.find(Boolean) || "";
};

const getHistoryOwnerIds = (value = {}) =>
    [
        getOtcRequestId(value),
        value.requestId,
        value.request_id,
        value.transactionId,
        value.transaction_id,
        value.otc?._id,
        getHistoryRows(value).length ? value._id : null,
    ]
        .filter(Boolean)
        .map(String);

const normalizeOtcHistoryRow = (item = {}) => ({
    ...item,
    action: item.action || item.event || item.type || item.status,
    status: item.status || item.action || item.event,
    at: item.at || item.timestamp || item.createdAt || item.updatedAt,
    message: item.message || item.description || item.label,
    expiresAt: item.expiresAt || item.details?.expiresAt,
});

/**
 * Merge the latest OTC history-status response into an already formatted transaction.
 * The API has returned both direct history arrays and request wrappers over time, so the
 * extractor intentionally supports both without replacing a transaction's embedded
 * history when a matching request cannot be found.
 */
export const mergeOtcHistoryStatus = (txn, responsePayload) => {
    if (!txn?.isOtc) return txn;

    const payload = responsePayload?.data ?? responsePayload;
    const source = payload?.data ?? payload;
    const requestIds = [
        getOtcRequestId(txn),
        txn.id,
        txn.raw?._id,
        txn.raw?.transactionId,
    ]
        .filter(Boolean)
        .map(String);
    const requestReferences = [
        txn.otc?.reference,
        txn.raw?.TransactionReference,
        txn.raw?.RequestReference,
        txn.raw?.reference,
    ]
        .filter(Boolean)
        .map(String);
    const matchesTransaction = (item = {}) => {
        const idsMatch = getHistoryOwnerIds(item).some((id) => requestIds.includes(id));
        const references = [
            item.TransactionReference,
            item.RequestReference,
            item.transactionReference,
            item.requestReference,
            item.reference,
            item.otc?.reference,
        ]
            .filter(Boolean)
            .map(String);
        return idsMatch || references.some((reference) => requestReferences.includes(reference));
    };

    let owner = null;
    let history = [];

    if (Array.isArray(source)) {
        const wrapper = source.find(matchesTransaction);

        if (wrapper) {
            owner = wrapper;
            history = getHistoryRows(wrapper);
        } else {
            const directRows = source.filter((item) => {
                const ownerIds = getHistoryOwnerIds(item);
                return ownerIds.length === 0 || matchesTransaction(item);
            });
            const looksLikeHistory = directRows.some(
                (item) => item?.action || item?.event || item?.message || item?.timestamp
            );
            if (looksLikeHistory) history = directRows;
        }
    } else if (source && typeof source === "object") {
        const nestedCollections = [source.requests, source.records, source.results, source.items]
            .filter(Array.isArray)
            .flat();
        owner = nestedCollections.find(matchesTransaction);
        if (!owner && getHistoryRows(source).length) owner = source;
        history = getHistoryRows(owner || source);
    }

    const identifiableHistory = history.filter(
        (item) => getHistoryOwnerIds(item).length > 0
    );
    if (identifiableHistory.length) {
        const matchingHistory = identifiableHistory.filter(matchesTransaction);
        if (matchingHistory.length) history = matchingHistory;
    }

    if (!history.length) return txn;

    const normalizedHistory = history
        .filter(Boolean)
        .map(normalizeOtcHistoryRow)
        .filter((item) => item.action || item.message)
        .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
    if (!normalizedHistory.length) return txn;

    const newest = normalizedHistory[0] || {};
    const offerUpdate = normalizedHistory.find(
        (item) => item.offeredRate != null || item.details?.offeredRate != null
    );
    const acceptanceUpdate = normalizedHistory.find(
        (item) => normalizeKey(item.action) === "offer_accepted"
    );
    const offeredRate =
        owner?.offeredRate ??
        owner?.otc?.offeredRate ??
        offerUpdate?.offeredRate ??
        offerUpdate?.details?.offeredRate ??
        txn.otc?.offeredRate;
    const recipientAmount =
        owner?.offeredAmountTo ??
        owner?.otc?.offeredAmountTo ??
        acceptanceUpdate?.offeredAmountTo ??
        acceptanceUpdate?.details?.offeredAmountTo ??
        txn.otc?.recipientAmount;
    const nextOtc = {
        ...txn.otc,
        ...(owner?.otc || {}),
        status: owner?.status || owner?.otc?.status || newest.status || txn.otc?.status,
        offeredRate,
        recipientAmount,
        expiresAt:
            owner?.expiresAt ??
            owner?.otc?.expiresAt ??
            offerUpdate?.expiresAt ??
            offerUpdate?.details?.expiresAt ??
            txn.otc?.expiresAt,
        acceptedAt:
            owner?.acceptedAt ??
            owner?.otc?.acceptedAt ??
            acceptanceUpdate?.acceptedAt ??
            acceptanceUpdate?.details?.acceptedAt ??
            txn.otc?.acceptedAt,
        history: normalizedHistory,
        offeredRateText: formatRate(
            offeredRate,
            txn.otc?.fromCurrency,
            txn.otc?.toCurrency
        ),
        recipientAmountText: formatAmount(recipientAmount, txn.otc?.toCurrency),
    };
    const nextTxn = { ...txn, otc: nextOtc };
    nextTxn.otcId =
        getOtcRequestId(owner || {}) ||
        normalizedHistory.map(getOtcRequestId).find(Boolean) ||
        getOtcRequestId(txn);
    const otcState = getOtcTransactionState(nextTxn);

    return {
        ...nextTxn,
        otcState,
        status: otcState.displayStatus,
    };
};

const formatOtcTransaction = (txn = {}) => {
    const rawOtc = txn.otc;
    const otc = rawOtc && typeof rawOtc === "object" ? rawOtc : {};
    const state = getOtcTransactionState(txn);
    const fromCurrency = String(otc.currency || txn.currency || "").toUpperCase();
    const toCurrency = String(otc.currencyTo || txn.currencyTo || "").toUpperCase();
    const recipientAmount = otc.offeredAmountTo ?? otc.amountTo ?? txn.amountTo;
    const preferredRate = otc.customerRate;
    const standardRate = otc.currentRate ?? txn.rate;
    const offeredRate = otc.offeredRate;
    const rawRecipient = txn.recipient || otc.recipient || {};
    const receiverName =
        txn.receiverName ||
        txn.accountName ||
        txn.recipientName ||
        rawRecipient.account_owner_name ||
        rawRecipient.accountName;
    const bankName = txn.bankName || txn.bank_name || rawRecipient.bank_name || rawRecipient.bankName;
    const accountNumber =
        txn.accountNumber ||
        rawRecipient.accountNumber ||
        rawRecipient.account?.accountNumber ||
        rawRecipient.account?.account_number;
    const reference = txn.TransactionReference || txn.RequestReference || txn._id;
    const fee = txn.transactionFee ?? txn.fee;
    const history = [...(otc.history || [])]
        .filter((item) => item && (item.message || item.action))
        .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));

    if (state.phase === "expired" && !history.some((item) => normalizeKey(item.action) === "offer_expired")) {
        history.unshift({
            action: "offer_expired",
            status: "expired",
            at: otc.expiresAt || otc.updatedAt || txn.updatedAt,
            message: "Offer expired before you accepted",
        });
    }
    if (state.phase === "completed" && !history.some((item) => normalizeKey(item.action).includes("completed"))) {
        history.unshift({
            action: "completed",
            status: "completed",
            at: txn.updatedAt || txn.transactionDate,
            message: "You completed the OTC transfer",
        });
    }

    return {
        id: txn._id,
        otcId:
            txn.otcId ||
            txn.otc_id ||
            txn.otcRequestId ||
            (typeof rawOtc === "string" ? rawOtc : null) ||
            otc._id ||
            otc.id,
        isOtc: true,
        otcState: state,
        title: receiverName || "OTC transfer",
        receiverName: receiverName || "OTC transfer",
        transactionType: "otc-transfer",
        paymentType: txn.PaymentType || "debit",
        time: txn.transactionDate || otc.createdAt || txn.createdAt,
        status: state.displayStatus,
        amount: -Math.abs(Number(txn.amount ?? otc.amount ?? 0)),
        currency: fromCurrency,
        attachments: mergeAttachmentsUnique(txn.attachments || [], buildDocAttachments(txn)),
        raw: txn,
        otc: {
            ...otc,
            fromCurrency,
            toCurrency,
            preferredRate,
            standardRate,
            offeredRate,
            recipientAmount,
            history,
            preferredRateText: formatRate(preferredRate, fromCurrency, toCurrency),
            standardRateText: formatRate(standardRate, fromCurrency, toCurrency),
            offeredRateText: formatRate(offeredRate, fromCurrency, toCurrency),
            recipientAmountText: formatAmount(recipientAmount, toCurrency),
            feeText: fee == null ? "—" : formatAmount(fee, fromCurrency),
            currencyPair: fromCurrency && toCurrency ? `${fromCurrency} - ${toCurrency}` : "—",
            reference,
            receiverName,
            bankName,
            accountNumber,
            narration: txn.narration || "",
        },
        details: [],
    };
};

const buildBankIdentifiers = (txn = {}) => {
    const f = (v) => v != null && String(v).trim() !== "";

    const fields = [
        ["Account number", txn.accountNumber],
        ["Bank name", txn.bankName],
        ["Routing number", txn.routingNumber || txn.ABARouting || txn.aba],
        ["Transit number", txn.transitNumber],
        ["Institution number", txn.institutionNumber],
        ["Sort code", txn.sortCode],
        ["IBAN", txn.iban || txn.IBAN],
        ["SWIFT / BIC", txn.swiftCode || txn.swift || txn.bic],
        ["CLABE (MX)", txn.clabe],
        ["BSB (AU)", txn.bsb],
        ["IFSC (IN)", txn.ifsc],
    ];

    return fields
        .filter(([_, v]) => f(v))
        .map(([label, value]) => ({ label, value: String(value) }));
};

function pickUrl(objOrStr) {
    if (!objOrStr) return "";
    if (typeof objOrStr === "string") return objOrStr;
    if (typeof objOrStr === "object") return objOrStr.url || objOrStr.link || "";
    return "";
}

function pickName(objOrStr, fallback = "Document") {
    if (!objOrStr) return fallback;
    if (typeof objOrStr === "object" && objOrStr.name) return objOrStr.name;
    const url = pickUrl(objOrStr);
    try {
        const u = new URL(url);
        const last = decodeURIComponent(u.pathname.split("/").filter(Boolean).pop() || "");
        return last || fallback;
    } catch {
        return fallback;
    }
}

function pickSize(objOrStr) {
    if (!objOrStr || typeof objOrStr !== "object") return undefined;
    return objOrStr.size || objOrStr.fileSize || undefined;
}

function buildDocAttachments(txn = {}) {
    const candidates = [
        ["Invoice", txn.invoice ?? txn.invoiceUrl ?? txn.invoiceDocument ?? txn.Invoice],
        [
            "Other document",
            txn.supportDocument ??
                txn.supportingDocument ??
                txn.otherDocument ??
                txn.supportDoc ??
                txn.supportDocumentUrl,
        ],
    ];

    const out = [];
    for (const [label, raw] of candidates) {
        const url = pickUrl(raw);
        if (!url) continue;
        out.push({
            id: `${label.toLowerCase().replace(/\s+/g, "-")}-${url}`,
            label,
            url,
            fileName: pickName(raw, label),
            fileSize: pickSize(raw),
        });
    }
    return out;
}

function mergeAttachmentsUnique(base = [], extra = []) {
    const seen = new Set();
    const rows = [...(base || []), ...(extra || [])];
    return rows.filter((a) => {
        const key = (a.url || "") + "::" + (a.label || "");
        if (seen.has(key)) return false;
        seen.add(key);
        return Boolean(a?.url);
    });
}

export function formatTransactions(records = []) {
    return records.map((txn) => {
        if (
            normalizeKey(txn.TransactionType) === "otc" ||
            normalizeKey(txn.type) === "otc" ||
            normalizeKey(txn.source) === "otc" ||
            txn.otc
        ) {
            return formatOtcTransaction(txn);
        }

        const id = txn._id;
        const requestReference = txn.RequestReference;
        const route =
            txn.TransactionType === "Invoice"
                ? `/v2/payment/invoice/manage/${requestReference}`
                : `/flex/home/manage-po?orderId=${requestReference}&type=inbound`;
        const title =
            txn.TransactionType === "BalanceSwap"
                ? `Converted ${txn.currencyTo} to ${txn.currency}`
                : txn.accountName || txn.narration || "No Narration";

        const transactionType =
            txn.TransactionType === "TransactionFee"
                ? "TransactionFee"
                : txn.TransactionType === "BalanceSwap"
                ? "wallet-conversion"
                : txn.TransactionType === "FxTransfer"
                ? "bank-transfer"
                : txn.PaymentType === "credit"
                ? "wallet-funding"
                : txn.TransactionType === "OTC"
                ? "bank-transfer"
                : txn.TransactionType === "crossBorder"
                ? "bank-transfer"
                : "local-transfer";

        const time = txn.transactionDate;
        const status = normalizeStatus(txn.status, txn.paymentStatus);
        const amount = txn.PaymentType === "debit" ? -txn.amount : txn.amount;
        const currency = txn.currency;
        const attachments = txn.attachments || [];
        const paymentType = txn.PaymentType;
        const reasonForFailure = txn?.reason;

        {
            // Normalize types (include TransactionType)
            const rawType = txn.TransactionType ?? txn.transactionType ?? txn.type ?? "";
            const subType =
                txn.TransactionSubType ??
                txn.transactionSubType ??
                txn.subType ??
                txn.subtype ??
                "";
            const category = txn.TransactionCategory ?? "";

            const tNorm = String(rawType || category || subType).toLowerCase();
            const isCrypto = tNorm === "cryptotransfer";

            // currencies (used for safe fallback)
            const fromCurr = (txn.currency || txn.fromCurrency || "").toString().toUpperCase();
            const toCurr = (
                txn.currencyTo ||
                txn.recipientCurrency ||
                txn.payoutCurrency ||
                txn?.recipient?.currency ||
                ""
            )
                .toString()
                .toUpperCase();

            const looksOtcOrCross =
                tNorm === "otc" ||
                tNorm === "crossborder" ||
                tNorm === "cross-border" ||
                tNorm === "cross_border" ||
                tNorm === "fxtransfer" ||
                txn.isOTC === true ||
                txn.isCrossBorder === true ||
                (!!fromCurr && !!toCurr && fromCurr !== toCurr && !isCrypto);

            if (looksOtcOrCross) {
                const recipientGetsNum = Number(
                    txn.amountTo ?? txn.toAmount ?? txn.recipientAmount ?? 0
                );
                const recipientGets =
                    toCurr && Number.isFinite(recipientGetsNum)
                        ? `${recipientGetsNum.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                          })} ${toCurr}`
                        : "—";

                const rate =
                    txn.rate ?? txn.exchangeRate ?? txn.conversionRate ?? txn.fxRate ?? null;
                const rateStr =
                    rate != null && fromCurr && toCurr ? `1 ${fromCurr} = ${rate} ${toCurr}` : "—";

                const rawFee = txn.transactionFee ?? txn.fee ?? null;
                const feeStr =
                    rawFee != null
                        ? typeof rawFee === "number"
                            ? `${rawFee.toLocaleString()} ${fromCurr || ""}`.trim()
                            : String(rawFee)
                        : "—";

                const pair = fromCurr && toCurr ? `${fromCurr} - ${toCurr}` : "—";

                const receiverLine1 =
                    txn.receiverName || txn.accountName || txn.recipientName || txn.network || txn.networkTo || "—";
                const receiverLine2 =
                    txn.accountNumber && txn.bankName
                        ? `${txn.accountNumber} • ${txn.bankName}`
                        : txn.accountNumber || txn.bankName || "—";

                const referenceId = txn.TransactionReference ?? txn.RequestReference ?? "—";
                const remark = getRemark(txn);
            
                const details = [
                    { label: "Recipient receives", value: recipientGets },
                    { label: "Exchange rate at conversion", value: rateStr },
                    { label: "Fee", value: feeStr },
                    { label: "Currency Pair", value: pair },
                    { label: "Type", value: "Bank Transfer" },
                    { label: "Reference", value: referenceId },
                    {
                        label: "Receiver",
                        value: (
                            <div className="flex flex-col text-end">
                                <span>{receiverLine1}</span>
                                <span className="text-sm text-[#6F739B] font-normal">
                                    {receiverLine2}
                                </span>
                            </div>
                        ),
                    },
                    ...buildBankIdentifiers(txn),
                    { label: "Remark", value: remark },
                ];


                const docAttachments = buildDocAttachments(txn);
                const finalAttachments = mergeAttachmentsUnique(
                    txn.attachments || [],
                    docAttachments
                );

                return {
                    id: txn._id,
                    title:
                        txn.TransactionType === "BalanceSwap"
                            ? `Converted ${txn.currencyTo} to ${txn.currency}`
                            : txn.accountName || txn.narration || "No Narration",
                    transactionType: "bank-transfer",
                    paymentType: txn.PaymentType,
                    time: txn.transactionDate,
                    status,
                    amount: txn.PaymentType === "debit" ? -txn.amount : txn.amount,
                    currency: txn.currency,
                    details,
                    attachments: finalAttachments,
                    reasonForFailure,
                };
            }
        }

        // Bank / TradeVu / Fee
        if (
            ["BankTransfer", "FxTransfer", "TradevutoTradevu", "TransactionFee"].includes(
                txn.TransactionType
            )
        ) {
            const receiverLine1 = txn.receiverName || txn.accountName || "—";
            const receiverLine2 =
                txn.accountNumber && txn.bankName
                    ? `${txn.accountNumber} • ${txn.bankName}`
                    : txn.accountNumber || txn.bankName || "—";

            const isBankLike =
                txn.TransactionType === "BankTransfer" || txn.TransactionType === "FxTransfer";
            const isCredit = txn.PaymentType === "credit";
            const labelKey = isCredit ? "Sender" : "Receiver";

            const details = [
                {
                    label: "Type",
                    value: isBankLike
                        ? "Bank Transfer"
                        : txn.TransactionType === "TradevutoTradevu"
                        ? "TradeVu-to-TradeVu Transfer"
                        : "Fee",
                },
                { label: "Reference", value: txn.TransactionReference },
                ...(isBankLike
                    ? [
                          {
                              label: labelKey,
                              value: (
                                  <div className="flex flex-col text-end">
                                      <span>{receiverLine1}</span>
                                      <span className="text-sm text-[#6F739B] font-normal">
                                          {receiverLine2}
                                      </span>
                                  </div>
                              ),
                          },
                      ]
                    : txn.TransactionType === "TradevutoTradevu"
                    ? [
                          {
                              label: labelKey,
                              value: isCredit
                                  ? txn.senderName || txn.accountName || "—"
                                  : txn.receiverName || txn.accountName || "—",
                          },
                      ]
                    : []),
                ...buildBankIdentifiers(txn),
                { label: "Remark", value: getRemark(txn) },
            ];

            const docAttachments = buildDocAttachments(txn);
            const finalAttachments = mergeAttachmentsUnique(txn.attachments || [], docAttachments);

            return {
                id,
                title,
                transactionType,
                paymentType,
                time,
                status,
                amount,
                currency,
                details,
                attachments: finalAttachments, reasonForFailure
            };
        }

        // Crypto transfers (credit or debit)
        if (txn.TransactionType === "CryptoTransfer") {
            const sub = String(
                txn.TransactionSubType || txn.transactionSubType || txn.subType || txn.subtype || ""
            ).toUpperCase();

            const isCredit = String(txn.PaymentType || "").toLowerCase() === "credit";

            if (isCredit) {
                const typeLabel = "Credit";
                const network = txn.network ?? txn.Network ?? txn.chain ?? txn.blockchain ?? "—";
                const creditedAddress =
                    txn.accountNumber ?? txn.address ?? txn.to ?? txn.toAddress ?? "—";
                const sendersAddress =
                    txn.accountName ?? txn.senderAddress ?? txn.from ?? txn.fromAddress ?? "—";
                const txHash = txn.hash ?? txn.transactionHash ?? txn.txHash ?? "—";
                const referenceId = txn.TransactionReference ?? txn.RequestReference ?? "—";

                const details = [
                    { label: "Type", value: typeLabel },
                    { label: "Network", value: network },
                    { label: "Address", value: creditedAddress },
                    { label: "Sender’s Address", value: sendersAddress },
                    { label: "Transaction hash", value: txHash },
                    { label: "Reference ID", value: referenceId },
                ];

                const docAttachments = buildDocAttachments(txn);
                const finalAttachments = mergeAttachmentsUnique(
                    txn.attachments || [],
                    docAttachments
                );

                return {
                    id,
                    title,
                    transactionType,
                    paymentType,
                    time,
                    status,
                    amount,
                    currency,
                    details,
                    attachments: finalAttachments, reasonForFailure
                };
            }

            if (sub === "TOCRYPTO") {
                const typeLabel = "CryptoTransfer";
                const network = txn.network ?? txn.Network ?? txn.chain ?? txn.blockchain ?? "—";
                const toAddress = txn.accountNumber ?? txn.address ?? txn.to ?? "—";
                const transactionFee = txn.transactionFee ?? "—";
                const txHash = txn.hash ?? txn.transactionHash ?? txn.txHash ?? "—";
                const referenceId = txn.TransactionReference ?? txn.RequestReference ?? "—";

                const details = [
                    { label: "Type", value: typeLabel },
                    { label: "Network", value: network },
                    { label: "Fee", value: transactionFee },
                    { label: "Sent to", value: toAddress },
                    { label: "Transaction hash", value: txHash },
                    { label: "Reference ID", value: referenceId },
                ];

                const docAttachments = buildDocAttachments(txn);
                const finalAttachments = mergeAttachmentsUnique(
                    txn.attachments || [],
                    docAttachments
                );

                return {
                    id,
                    title,
                    transactionType,
                    paymentType,
                    time,
                    status,
                    amount,
                    currency,
                    details,
                    attachments: finalAttachments, reasonForFailure
                };
            }

            if (sub === "TOFIAT") {
                const fromCurr = (txn.currency || txn.fromCurrency || "USDC")
                    .toString()
                    .toUpperCase();
                const toCurr = (
                    txn.currencyTo ||
                    txn.recipientCurrency ||
                    txn.payoutCurrency ||
                    txn?.recipient?.currency ||
                    ""
                )
                    .toString()
                    .toUpperCase();

                const recipientGetsNum = Number(
                    txn.amountTo ?? txn.toAmount ?? txn.recipientAmount ?? 0
                );
                const recipientGets =
                    toCurr && isFinite(recipientGetsNum)
                        ? `${recipientGetsNum.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                          })} ${toCurr}`
                        : "—";

                const rate =
                    txn.rate ?? txn.exchangeRate ?? txn.conversionRate ?? txn.fxRate ?? null;
                const rateStr = rate != null ? `1 ${fromCurr} = ${rate} ${toCurr}` : "—";

                const rawFee = txn.transactionFee ?? txn.fee ?? null;
                const feeStr = typeof rawFee === "number" ? `${rawFee} ${fromCurr}` : rawFee || "—";

                const pair = fromCurr && toCurr ? `${fromCurr} - ${toCurr}` : "—";

                const receiverLine1 =
                    txn.receiverName || txn.accountName || txn.recipientName || "—";
                const receiverLine2 =
                    txn.accountNumber && txn.bankName
                        ? `${txn.accountNumber} • ${txn.bankName}`
                        : txn.accountNumber || txn.bankName || "—";

                const referenceId = txn.TransactionReference ?? txn.RequestReference ?? "—";
                const remark = getRemark(txn);

                const details = [
                    { label: "Recipient receives", value: recipientGets },
                    { label: "Exchange rate at conversion", value: rateStr },
                    { label: "Fee", value: feeStr },
                    { label: "Currency Pair", value: pair },
                    { label: "Type", value: "Bank transfer" },
                    {
                        label: "Receiver",
                        value: (
                            <div className="flex flex-col text-end">
                                <span>{receiverLine1}</span>
                                <span className="text-sm text-[#6F739B] font-normal">
                                    {receiverLine2}
                                </span>
                            </div>
                        ),
                    },
                    { label: "Reference ID", value: referenceId },
                    ...buildBankIdentifiers(txn),
                    { label: "Remark", value: remark },
                ];

                const docAttachments = buildDocAttachments(txn);
                const finalAttachments = mergeAttachmentsUnique(
                    txn.attachments || [],
                    docAttachments
                );

                return {
                    id,
                    title,
                    transactionType,
                    paymentType,
                    time,
                    status,
                    amount,
                    currency,
                    details,
                    attachments: finalAttachments,
                    reasonForFailure
                };
            }
        }

        // Order / Invoice / Shipment
        if (["Order", "Invoice", "Shipment"].includes(txn.TransactionType)) {
            const isOrder = txn.TransactionType === "Order";
            const firstLabel = isOrder
                ? "Purchase order"
                : txn.TransactionType === "Invoice"
                ? "Invoice"
                : "Shipment";

            const details = [
                {
                    label: firstLabel,
                    value: (
                        <Link to={route} className="!text-[#6F00FF] hover:underline">
                            {txn.TransactionType} {txn.narration} <ExportSquare size="15" />
                        </Link>
                    ),
                },
                { label: "Type", value: txn.TransactionType },
                { label: "Reference", value: txn.TransactionReference },
                { label: "Sender", value: txn.senderName || txn.accountName },
                { label: "Remark", value: getRemark(txn) },
            ];

            const docAttachments = buildDocAttachments(txn);
            const finalAttachments = mergeAttachmentsUnique(txn.attachments || [], docAttachments);

            return {
                id,
                title,
                transactionType,
                paymentType,
                time,
                status,
                amount,
                currency,
                details,
                attachments: finalAttachments, reasonForFailure
            };
        }

        // Fallback (e.g. pure BalanceSwap)
        return {
            id,
            title,
            transactionType,
            paymentType,
            time,
            status,
            amount,
            currency,
            details: [
                { label: "Amount converted", value: txn.amountTo },
                { label: "Exchange rate", value: txn.rate },
                { label: "Fee", value: txn.feeDisplay },
                { label: "Currency Pair", value: txn.currencyTo && txn.currency ? `${txn.currencyTo} – ${txn.currency}` : null },
                { label: "Type", value: txn.PaymentType },
                { label: "Reference", value: txn._id },
                { label: "Remark", value: getRemark(txn) },
            ],
            attachments,
            reasonForFailure
        };
    });
}

export function groupByDate(formatted = []) {
    const sections = new Map();

    const labelFor = (time) => {
        const d = dayjs(time);
        if (d.isSame(dayjs(), "day")) return "Today";
        if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
        if (d.isAfter(dayjs().subtract(7, "day"))) return d.format("dddd");
        return d.format("ddd MMM Do, YYYY");
    };

    formatted.forEach((txn) => {
        const label = labelFor(txn.time);
        if (!sections.has(label)) sections.set(label, []);
        sections.get(label).push(txn);
    });

    return sections;
}
